import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Download, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HTC Lead-Magnet Formular.
 * Speichert Lead + liefert direkten Download-Link zurück.
 * Newsletter-Opt-in ist STRIKT separat (nicht vorangekreuzt).
 */
export default function HtcLeadMagnetForm() {
  const [form, setForm] = useState({ name: "", email: "", datenschutz_ok: false, newsletter_opt_in: false });
  const [state, setState] = useState({ loading: false, error: "", success: null });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", success: null });
    try {
      const res = await fetch(`${API}/api/leads/htc-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || "Fehler beim Senden");
      setState({ loading: false, error: "", success: data });
    } catch (err) {
      setState({ loading: false, error: err.message, success: null });
    }
  };

  const canSubmit = form.name.trim().length >= 2 && form.email.includes("@") && form.datenschutz_ok;

  if (state.success) {
    return (
      <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.05] p-8 text-center" data-testid="htc-leadmagnet-success">
        <CheckCircle2 size={40} className="text-[#D4AF37] mx-auto mb-4" />
        <h3 className="serif text-2xl font-light mb-3">Danke, dein Kompass wartet.</h3>
        <p className="text-white/70 text-[15px] mb-6 leading-relaxed">
          Wir haben dir den {state.success.title} an <span className="text-[#D4AF37]">{form.email}</span> gesendet.
          Du kannst ihn außerdem direkt hier öffnen.
        </p>
        <a
          href={state.success.download_url.startsWith("http") ? state.success.download_url : `${window.location.origin}${state.success.download_url}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex"
          data-testid="htc-leadmagnet-download-btn"
        >
          <Download size={16} /> PDF öffnen
        </a>
        {state.success.mail_status && state.success.mail_status !== "sent" && (
          <p className="text-white/40 text-xs italic mt-4">
            Hinweis: E-Mail-Versand aktuell nicht aktiviert — bitte nutze den direkten Download.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 lg:p-8 space-y-5" data-testid="htc-leadmagnet-form">
      <div className="overline">SAT-HTC Kompass · kostenlose Einführung</div>
      <p className="text-white/65 text-sm leading-relaxed">
        Trage deinen Namen und deine E-Mail ein, um die Einführung in den Human Transformation Code zu erhalten.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <input required minLength={2} maxLength={80} type="text" placeholder="Dein Name"
               value={form.name} onChange={upd("name")}
               className="w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30"
               data-testid="htc-leadmagnet-name" />
        <input required type="email" placeholder="du@beispiel.com"
               value={form.email} onChange={upd("email")}
               className="w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30"
               data-testid="htc-leadmagnet-email" />
      </div>

      <div className="space-y-2.5">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={form.datenschutz_ok} onChange={upd("datenschutz_ok")}
                 className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" data-testid="htc-leadmagnet-consent-privacy" />
          <span className="text-[12px] text-white/65 leading-relaxed">
            Ich habe die <Link to="/datenschutz" className="text-[#D4AF37] hover:underline">Datenschutzerklärung</Link> gelesen
            und stimme der Nutzung meiner E-Mail zur Zustellung des SAT-HTC Kompass zu. *
          </span>
        </label>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={form.newsletter_opt_in} onChange={upd("newsletter_opt_in")}
                 className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" data-testid="htc-leadmagnet-consent-newsletter" />
          <span className="text-[12px] text-white/55 leading-relaxed">
            Zusätzlich möchte ich gelegentliche SAT-Impulse per E-Mail erhalten (jederzeit abbestellbar).
          </span>
        </label>
      </div>

      {state.error && (
        <div className="flex items-start gap-3 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-xs text-red-200" data-testid="htc-leadmagnet-error">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <div>{state.error}</div>
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit || state.loading}
        className="btn-primary w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="htc-leadmagnet-submit"
      >
        {state.loading ? (<><Loader2 size={16} className="animate-spin" /> Wird gesendet …</>) : (<>Kompass anfordern <ArrowRight size={16} /></>)}
      </button>
    </form>
  );
}
